import React from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';

import AttendeeTicketNotification from '@eb/attendee-ticket-notification';
import { getWindowObject } from '@eb/feature-detection';
import { constants as destinationConstants } from '@eb/destination-utils';
import { trackAnalyticsEvent } from '@eb/site-analytics';
import { isMediumNarrowDown } from 'eventbrite_design_system/utils/mediaQueries';

import {
    BUCKETS_PROPTYPES,
    FEATURE_FLAGS_PROPTYPES,
    ORGANIZER_ARTICLE_BUCKET_PROPTYPE,
    FLAT_BUCKET_PROPTYPE,
} from '../constants/propTypes';
import {
    GA_HOME_FEED_CATEGORY,
    GA_VIEW_HOMEPAGE_FEED,
    GA_VIEW_LOGGEDOUT_HOMEPAGE_FEED,
    GA_HOME_CATEGORY,
    GA_EXPERIMENT_EXPOSURE,
} from '../constants/analytics';
import { storeHeroImageIndex } from '../utils/localStorage';
import { LUX_LABEL_FEED } from '../constants/constants';

import {
    enrollInSearchBarOnHomeOptimizeExperiment,
    shouldOverrideExperimentVariantB,
    shouldOverrideExperimentVariantC,
} from '../components/SearchAndLocationBar/utils';

const Layout = loadable(() => import('eventbrite_design_system/layout/Layout'));
const ConnectedBasePage = loadable(() => import('./ConnectedBasePage'));
const EnhancedHomeConsumerHeader = loadable(() =>
    import('../components/EnhancedHomeConsumerHeader'),
);

const ConnectedHeader = loadable(() => import('../containers/ConnectedHeader'));
const BrowseContainer = loadable(() => import('../containers/BrowseContainer'));

const ProtestMap = loadable(() =>
    import('../components/ProtestMap/ProtestMap'),
);
const CoronaVirusBanner = loadable(() =>
    import('../components/Banners/CoronaVirusBanner'),
);
const NIVABanner = loadable(() => import('../components/Banners/NIVABanner'));

const SearchAndLocationBar = loadable(() =>
    import('../components/SearchAndLocationBar/SearchAndLocationBar'),
);

const { PLACE_OBJECT_PROPTYPE } = destinationConstants;

// This is mainly here because on the feed we want to track extra stuff
// on componentDidMount in addition to the pageview we already are tracking,
// and we want to keep it consistent where the category will be different depending
// on logged-in or logged-out status.
const getFeedTrackingProperties = (isAuthenticated, userId, experimentId) => {
    let category = GA_HOME_CATEGORY;

    let action = GA_VIEW_LOGGEDOUT_HOMEPAGE_FEED;

    let dimensions = {};

    if (isAuthenticated) {
        category = GA_HOME_FEED_CATEGORY;
        action = GA_VIEW_HOMEPAGE_FEED;
        dimensions = { userId };
    }

    if (experimentId) {
        dimensions = {
            ...dimensions,
            experimentId,
        };

        action = GA_EXPERIMENT_EXPOSURE;
    }

    return {
        category,
        action,
        nonInteraction: true,
        dimensions,
    };
};

const trackSpeedCurveLUX = () => {
    const LUX = getWindowObject('LUX');

    if (LUX) {
        LUX.label = LUX_LABEL_FEED;
    }
};

const applyIEFixes = () => {
    /**
     * IE is returning errors because SVG elements are not correctly mapped when parsing the docs.
     * This post gives a solution to this: https://github.com/Pomax/react-onclickoutside#ie-does-not-support-classlist-for-svg-elements
     */
    if (!('classList' in SVGElement.prototype)) {
        Object.defineProperty(SVGElement.prototype, 'classList', {
            get() {
                return {
                    contains: (className) =>
                        this.className.baseVal.split(' ').indexOf(className) !==
                        -1,
                };
            },
        });
    }
};

// Deal with the messed up Varnish urls (/esi_cahe/?...) which break the signin link
const fixLoginUrl = (loginUrl) => {
    const splitUrl = loginUrl.split('/');
    const urlBeginning = splitUrl.slice(0, -1);

    return urlBeginning.concat('?referrer=%2F').join('/');
};

export default class FeedPage extends React.Component {
    static propTypes = {
        // feed props
        buckets: BUCKETS_PROPTYPES,
        featureFlags: FEATURE_FLAGS_PROPTYPES,
        placeId: PropTypes.string,
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        currentPlace: PropTypes.string,
        currentPlaceParent: PropTypes.string,
        locations: PropTypes.shape({
            places: PropTypes.arrayOf(PLACE_OBJECT_PROPTYPE),
        }),
        placeType: PropTypes.string,
        slug: PropTypes.string,
        fiveToFollow: PropTypes.arrayOf(ORGANIZER_ARTICLE_BUCKET_PROPTYPE),
        flatBucket: FLAT_BUCKET_PROPTYPE,
        isAuthenticated: PropTypes.bool,
        userId: PropTypes.string,
        handleRestOfContext: PropTypes.func,
        originalContext: PropTypes.object,
        history: PropTypes.object,
        env: PropTypes.object,
    };

    static defaultProps = {
        handleRestOfContext: () => null,
    };

    state = {
        isInBrowser: false,
        // Whether user is enrolled in the "search bar on homepage" experiment with Optimize
        // we don't want to default to variant A because we want to show something different than
        // A while we wait for Optimize to return
        isInSearchBarExperimentVariant: null,
        isMobile: false,
    };

    componentDidMount() {
        const {
            isAuthenticated,
            userId,
            handleRestOfContext,
            originalContext,
            env,
        } = this.props;

        trackSpeedCurveLUX();

        applyIEFixes();

        const feedTrackingProperties = getFeedTrackingProperties(
            isAuthenticated,
            userId,
        );

        trackAnalyticsEvent(feedTrackingProperties);

        handleRestOfContext(originalContext);

        // Updates the last hero image seen to get a new one.
        storeHeroImageIndex();

        this.setState({
            isInBrowser: true,
            isMobile: isMediumNarrowDown(),
        });

        const isDotCom = env.serverUrl.endsWith('.com');

        if (isDotCom) {
            ProtestMap.preload();
        }

        this._handleEnrollInSearchBarExperiment();
    }

    _handleEnrollInSearchBarExperiment = () => {
        enrollInSearchBarOnHomeOptimizeExperiment(
            this._setIsInSearchBarExperiment,
        );

        setTimeout(() => {
            // Just to be extra-safe, if we still have no variant set after a long time, just
            // show them the content for A. This can happen when the Optimize experiment hasn't started yet,
            // it will never call our callback which sets the variant away from undefined.
            if (!this.state.isInSearchBarExperimentVariant) {
                this.setState({ isInSearchBarExperimentVariant: 'A' });
            }
        }, 2000);
    };

    _setIsInSearchBarExperiment = (variant) => {
        const { isAuthenticated, userId } = this.props;

        this.setState({ isInSearchBarExperimentVariant: variant });

        const experimentId = JSON.stringify({
            HomeSearchFocusExp: variant,
        });

        const feedTrackingProperties = getFeedTrackingProperties(
            isAuthenticated,
            userId,
            experimentId,
        );

        trackAnalyticsEvent(feedTrackingProperties);
    };

    render() {
        // During the server render, because of Varnish, we want to render the page
        // as a logged-out user. Once we are in the browser, we have the props for the user
        // and we want to update the page. Since most of our components do not keep this in mind,
        // we basically have to do it with componentDidMount or else we get weird markup mismatch issues.
        // Once we're in the browser, we'll go to use the normal props.
        // See also the ConsumerHeader/index.js
        let overrideProps = this.state.isInBrowser
            ? {}
            : {
                  env: {
                      ...this.props.env,
                      loginUrl: fixLoginUrl(this.props.env.loginUrl),
                      showCookieHeader: false,
                  },
                  footer: <div />,
                  user: {
                      isAuthenticated: false,
                      publicId: null,
                      canCreateEvents: true,
                  },
              };

        const isDotCom = this.props.env.serverUrl.endsWith('.com');

        const { isInSearchBarExperimentVariant } = this.state;

        // Even though A is the null variant, we still want to start initially hiding the elements
        // even if they are used by the null variant. This is to avoid a jarring experience where elements disappear
        // off the page if you are enrolled in B. Better to start out with things being hidden and wait for
        // the Optimize call to return. If we are in an environment without Optimize (ad blocking), then we can also
        // just show the null variant.
        let hideExistingSearchBar = true;
        let showNewSearchBar = false;

        if (isInSearchBarExperimentVariant === 'A') {
            hideExistingSearchBar = false;
            showNewSearchBar = false;
        }

        if (
            isInSearchBarExperimentVariant === 'B' ||
            shouldOverrideExperimentVariantB()
        ) {
            hideExistingSearchBar = true;
            showNewSearchBar = true;
        }

        if (
            isInSearchBarExperimentVariant === 'C' ||
            shouldOverrideExperimentVariantC()
        ) {
            hideExistingSearchBar = false;
            showNewSearchBar = true;
        }

        return (
            <ConnectedBasePage
                {...this.props}
                {...overrideProps}
                isHomePage={true}
                structureHeader={EnhancedHomeConsumerHeader}
                hideSearchBar={hideExistingSearchBar}
            >
                <Layout hasHorizontalGutters={true} maxWidth="large">
                    <AttendeeTicketNotification
                        shouldAnimateEntry={true}
                        isAuthenticated={this.props.isAuthenticated}
                        containerStyles={
                            this.state.isMobile
                                ? { margin: '0 auto', top: '120px' }
                                : { right: '32px', top: '120px' }
                        }
                        onLinkClick={(eid, hasDigitalContent) =>
                            trackAnalyticsEvent({
                                category: GA_HOME_FEED_CATEGORY,
                                label: eid,
                                action: hasDigitalContent
                                    ? 'UpcomingOnlineEventViewed'
                                    : 'UpcomingTicketViewed',
                            })
                        }
                        onDismiss={(timeUntilEvent, hasDigitalContent) =>
                            trackAnalyticsEvent({
                                category: GA_HOME_FEED_CATEGORY,
                                label: timeUntilEvent,
                                action: hasDigitalContent
                                    ? 'UpcomingOnlineEventDismissed'
                                    : 'UpcomingTicketDismissed',
                            })
                        }
                    />
                    {isDotCom ? (
                        <>
                            <div className="eds-l-pad-top-2">
                                <NIVABanner />
                            </div>
                            <ConnectedHeader history={this.props.history} />
                        </>
                    ) : (
                        <>
                            <div className="eds-l-pad-top-2">
                                <CoronaVirusBanner />
                            </div>
                            <ConnectedHeader history={this.props.history} />
                        </>
                    )}
                    {showNewSearchBar && (
                        <SearchAndLocationBar
                            env={this.props.env}
                            handleSearch={this.props.handleSearch}
                            onSearchSelect={this.props.onSearchSelect}
                        />
                    )}
                    <BrowseContainer />
                </Layout>
            </ConnectedBasePage>
        );
    }
}
